// What the playground offers, in one place.
//
// The page talks to the Integrated Test Bed endpoints of demo-issuer-portal-backend
// (/api/public/itb/*). Those are anonymous: no Keycloak login, no prior request.
//
// Titles and descriptions are NOT here — they live in src/locales/*.json under
// `credentials.<credentialType>` and `proofs.<queryId>`, so they can be translated.
// Adding an entry here without the matching translation block shows the raw key.

export interface IssuableCredential {
    // Maps to DemoCredential.templateId. The backend looks up a row with
    // customerId = 'ITB_CUSTOMER_ID' and this templateId for the current tenant,
    // so every entry here needs a matching seed row or you get "Template not found".
    // It must also be an existing credentialConfigurationId in the wallet.
    credentialType: string;
}

export interface VerifiableProof {
    // Maps to the query_id of a presentation definition in the wallet.
    queryId: string;
    // credentialType values the DCQL query demands. Every claim in a DCQL query is
    // mandatory unless marked optional, so a wallet missing one of these reports
    // that it has nothing to share — without saying which one. The page names them.
    requiredCredentials: string[];
}

export const issuableCredentials: IssuableCredential[] = [
    // Two templates issue a Pid: this one (rvig) and 'pid_vc+sd-jwt' (pid). Both carry
    // vct 'Pid', which is what the DCQL query matches on, so either works.
    //
    // Note that 'Pid' is not a canonical EUDI identifier — the SD-JWT VC vct is
    // 'urn:eudi:pid:1' and the mdoc doctype is 'eu.europa.ec.eudi.pid.1'. A verifier
    // on playground.france-identite.gouv.fr queries the canonical values, so it will
    // never match these credentials. Changing that is a wallet-side change; see
    // docs/france-playground.md.
    {credentialType: 'pid'},
    {credentialType: 'rijbewijs'},
    {credentialType: 'iban'},
];

// The definition 'rijbewijs' is left out on purpose: it asks for vct
// 'urn:eu:credential:mobile-driving-licence:1' while the RDW template issues
// 'urn:eu:credential:mdl:1', so no wallet can ever satisfy it.
export const verifiableProofs: VerifiableProof[] = [
    // Both mandatory, so the wallet offers nothing until the visitor has issued the
    // Personal ID *and* the driving licence.
    {queryId: 'test', requiredCredentials: ['pid', 'rijbewijs']},
    {queryId: 'fr-age-18', requiredCredentials: []},
];

// The store buttons only render once these are filled in. Leaving them empty is a
// valid state: the visitor is then pointed at France Identité instead, which is the
// wallet the French playground audience already has.
//
// No country code in the App Store link on purpose: apps.apple.com/app/id<id>
// redirects to the visitor's own storefront, so a French visitor lands on the
// French one rather than the Dutch page the app was published from.
export const walletDownload = {
    appStoreUrl: 'https://apps.apple.com/app/id6753174105',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.credenco.wallet',
};

export const francePlaygroundUrl = 'https://playground.france-identite.gouv.fr/';

export const credencoWebsiteUrl = 'https://www.credenco.com/';

export const pollIntervalMs = 2000;
export const pollTimeoutMs = 180000;
